/**
 * bulk_select_controller.js — row multi-select for the Activity view (Stimulus).
 *
 * Backs the bulk status transition whose first real job is retiring a backlog
 * of stale planned actions. Doing that one row at a time is one page load per
 * action, which is why the backlog exists in the first place.
 *
 * Progressive enhancement, deliberately: the selection is real <input
 * type="checkbox" name="action_id"> elements inside a real <form> posting to
 * the bulk-status route. With this controller absent or broken the operator can
 * still tick rows and submit; all the controller adds is the select-all box,
 * the live count, and disabling a submit that would do nothing.
 *
 * It owns no write path. Every transition goes through the one POST handler,
 * which goes through MarketingService.bulk_update_action_status.
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ["checkbox", "toggleAll", "count", "submit", "status"];

    connect() {
        this.refresh();
    }

    /** Tick or untick every row at once. */
    toggleAll() {
        const checked = this.toggleAllTarget.checked;
        this.checkboxTargets.forEach((box) => {
            box.checked = checked;
        });
        this.refresh();
    }

    /**
     * Keep the header controls honest after any individual change.
     *
     * The select-all box goes indeterminate on a partial selection rather than
     * showing unticked: an unticked box next to twelve ticked rows reads as
     * "nothing is selected", which is exactly wrong before a bulk cancel.
     */
    refresh() {
        const selected = this.checkboxTargets.filter((box) => box.checked);

        if (this.hasToggleAllTarget) {
            const total = this.checkboxTargets.length;
            this.toggleAllTarget.checked = total > 0 && selected.length === total;
            this.toggleAllTarget.indeterminate =
                selected.length > 0 && selected.length < total;
        }
        if (this.hasCountTarget) {
            this.countTarget.textContent = selected.length
                ? `${selected.length} selected`
                : "";
        }
        if (this.hasSubmitTarget) {
            // A submit with nothing selected is a page reload that looks like a
            // failed bulk action.
            this.submitTarget.disabled =
                selected.length === 0 ||
                (this.hasStatusTarget && !this.statusTarget.value);
        }
    }
}
